/**
 * Vessel validation utility
 * Common validation rules for vessel data across Create, Update, and Clone actions
 */

import i18next from 'i18next'

/**
 * Validation configuration for vessel fields
 */
const VALIDATION_RULES = {
  accommodationBay: {
    max: 99,
    min: 1,
  },
  bay: {
    max: 99,
    min: 1,
  },
  engineBay: {
    max: 99,
    min: 1,
  },
  hatchCovers: {
    max: 4,
    min: 0,
  },
  imoNumber: {
    length: 7,
    max: 9999999,
    min: 1000000,
  },
  lowestDeckBay: {
    max: 99,
    min: 1,
  },
  tierRowRange: {
    max: 200,
    min: 0,
  },
  vesselName: {
    max: 30,
    min: 2,
  },
}

export { VALIDATION_RULES }

/**
 * Validate IMO Number
 * IMO numbers must be exactly 7 digits (1000000 - 9999999)
 */
const validateImoNumber = imoNumber => {
  // Convert to string if it's a number
  const imoStr = String(imoNumber || '').trim()

  if (!imoStr) {
    return {
      isValid: false,
      message: i18next.t('admin.vesselDrawer.validation.imoNumberRequired'),
    }
  }

  const imoNum = parseInt(imoStr, 10)

  if (!/^\d+$/.test(imoStr)) {
    return {
      isValid: false,
      message: i18next.t('admin.vesselDrawer.validation.imoNumberDigitsOnly'),
    }
  }

  if (imoStr.length !== VALIDATION_RULES.imoNumber.length) {
    return {
      isValid: false,
      message: i18next.t('admin.vesselDrawer.validation.imoNumberExactLength', {
        length: VALIDATION_RULES.imoNumber.length,
      }),
    }
  }

  if (
    imoNum < VALIDATION_RULES.imoNumber.min ||
    imoNum > VALIDATION_RULES.imoNumber.max
  ) {
    return {
      isValid: false,
      message: i18next.t('admin.vesselDrawer.validation.imoNumberRange', {
        max: VALIDATION_RULES.imoNumber.max,
        min: VALIDATION_RULES.imoNumber.min,
      }),
    }
  }

  return {
    isValid: true,
  }
}

/**
 * Validate vessel name
 */
const validateVesselName = vesselName => {
  if (!vesselName || vesselName.trim() === '') {
    return {
      isValid: false,
      message: i18next.t('admin.vesselDrawer.validation.vesselNameRequired'),
    }
  }

  if (vesselName.trim().length < 2) {
    return {
      isValid: false,
      message: i18next.t('admin.vesselDrawer.validation.vesselNameMinLength'),
    }
  }

  if (vesselName.trim().length > VALIDATION_RULES.vesselName.max) {
    return {
      isValid: false,
      message: i18next.t('admin.vesselDrawer.validation.vesselNameMaxLength'),
    }
  }

  return {
    isValid: true,
  }
}

/**
 * Validate abbreviation
 */
const validateAbbreviation = abbreviation => {
  if (!abbreviation || abbreviation.trim() === '') {
    return {
      isValid: false,
      message: i18next.t('admin.vesselDrawer.validation.abbreviationRequired'),
    }
  }

  if (abbreviation.trim().length < 2) {
    return {
      isValid: false,
      message: i18next.t('admin.vesselDrawer.validation.abbreviationMinLength'),
    }
  }

  return {
    isValid: true,
  }
}

/**
 * Validate bay number
 */
const validateBayNumber = (bay, fieldName = 'Bay') => {
  if (!bay || bay.trim() === '') {
    return {
      isValid: false,
      message: i18next.t('admin.vesselDrawer.validation.fieldRequired', {
        fieldName,
      }),
    }
  }

  const bayNum = parseInt(bay, 10)

  if (!/^\d+$/.test(bay.trim())) {
    return {
      isValid: false,
      message: i18next.t('admin.vesselDrawer.validation.fieldDigitsOnly', {
        fieldName,
      }),
    }
  }

  if (bayNum < VALIDATION_RULES.bay.min || bayNum > VALIDATION_RULES.bay.max) {
    return {
      isValid: false,
      message: i18next.t('admin.vesselDrawer.validation.fieldRange', {
        fieldName,
        max: VALIDATION_RULES.bay.max,
        min: VALIDATION_RULES.bay.min,
      }),
    }
  }

  return {
    isValid: true,
  }
}

/**
 * Validate hatch covers
 */
const validateHatchCovers = (hatchCovers, fieldName = 'Hatch Covers') => {
  if (!hatchCovers || hatchCovers.trim() === '') {
    return {
      isValid: false,
      message: i18next.t('admin.vesselDrawer.validation.fieldRequired', {
        fieldName,
      }),
    }
  }

  const hatchNum = parseInt(hatchCovers, 10)

  if (!/^\d+$/.test(hatchCovers.trim())) {
    return {
      isValid: false,
      message: i18next.t('admin.vesselDrawer.validation.fieldDigitsOnly', {
        fieldName,
      }),
    }
  }

  if (
    hatchNum < VALIDATION_RULES.hatchCovers.min ||
    hatchNum > VALIDATION_RULES.hatchCovers.max
  ) {
    return {
      isValid: false,
      message: i18next.t('admin.vesselDrawer.validation.fieldRange', {
        fieldName,
        max: VALIDATION_RULES.hatchCovers.max,
        min: VALIDATION_RULES.hatchCovers.min,
      }),
    }
  }

  return {
    isValid: true,
  }
}

/**
 * Validate optional bay fields (accommodation, engine, lowest deck)
 * @param {string} value - The field value
 * @param {string} fieldName - The field name for error messages
 * @param {object} rules - Validation rules
 * @param {boolean} isRequired - Whether the field is required
 */
const validateOptionalBayField = (
  value,
  fieldName,
  rules,
  isRequired = false
) => {
  // Check if required and empty
  if (isRequired && (!value || value.trim() === '')) {
    return {
      isValid: false,
      message: i18next.t('admin.vesselDrawer.validation.fieldRequired', {
        fieldName,
      }),
    }
  }

  // Optional field - empty is valid if not required
  if (!isRequired && (!value || value.trim() === '')) {
    return {
      isValid: true,
    }
  }

  const numValue = parseInt(value, 10)

  if (!/^\d+$/.test(value.trim())) {
    return {
      isValid: false,
      message: i18next.t('admin.vesselDrawer.validation.fieldDigitsOnly', {
        fieldName,
      }),
    }
  }

  if (numValue < rules.min || numValue > rules.max) {
    return {
      isValid: false,
      message: i18next.t('admin.vesselDrawer.validation.fieldRange', {
        fieldName,
        max: rules.max,
        min: rules.min,
      }),
    }
  }

  return {
    isValid: true,
  }
}

/**
 * Validate bay configurations array
 */
const validateBayConfigurations = (bayConfigurations, configType = 'Bay') => {
  const errors = []

  bayConfigurations.forEach((config, index) => {
    const bayLabel = `${configType} #${index + 1}`

    const bayValidation = validateBayNumber(config.bay, bayLabel)
    if (!bayValidation.isValid) {
      errors.push(bayValidation.message)
    }

    const hatchValidation = validateHatchCovers(
      config.hatchCovers,
      `${bayLabel} Hatch Covers`
    )
    if (!hatchValidation.isValid) {
      errors.push(hatchValidation.message)
    }
  })

  return {
    isValid: errors.length === 0,
    messages: errors,
  }
}

/**
 * Main validation function for vessel data
 * Used by Create, Update, and Clone actions
 * @param {boolean} isCreateMode - Whether this is a create operation (makes certain fields required)
 */
export const validateVesselData = ({
  accommodationBay = '',
  bayConfigurations = [],
  engineBay = '',
  isCreateMode = false,
  selectedRecord = {},
  twentiesConfigurations = [],
}) => {
  const errors = []

  // Validate vessel basic information
  const vesselNameValidation = validateVesselName(selectedRecord.vesselName)
  if (!vesselNameValidation.isValid) {
    errors.push(vesselNameValidation.message)
  }

  const abbreviationValidation = validateAbbreviation(
    selectedRecord.vesselAbbreviation || selectedRecord.abbreviation
  )
  if (!abbreviationValidation.isValid) {
    errors.push(abbreviationValidation.message)
  }

  const imoValidation = validateImoNumber(selectedRecord.imoNumber)
  if (!imoValidation.isValid) {
    errors.push(imoValidation.message)
  }

  // Validate bay configurations - at least one bay required on create
  if (
    isCreateMode &&
    bayConfigurations.length === 0 &&
    twentiesConfigurations.length === 0
  ) {
    errors.push(
      i18next.t('admin.vesselDrawer.validation.atLeastOneBayRequired')
    )
  }

  const fortyBaysValidation = validateBayConfigurations(
    bayConfigurations,
    'Forty Bay'
  )
  if (!fortyBaysValidation.isValid) {
    errors.push(...fortyBaysValidation.messages)
  }

  const twentiesBaysValidation = validateBayConfigurations(
    twentiesConfigurations,
    'Twenty Bay'
  )
  if (!twentiesBaysValidation.isValid) {
    errors.push(...twentiesBaysValidation.messages)
  }

  // Validate bay fields - required on create mode
  const accommodationValidation = validateOptionalBayField(
    accommodationBay,
    'Accommodation Bay',
    VALIDATION_RULES.accommodationBay,
    isCreateMode
  )
  if (!accommodationValidation.isValid) {
    errors.push(accommodationValidation.message)
  }

  const engineValidation = validateOptionalBayField(
    engineBay,
    'Engine Bay',
    VALIDATION_RULES.engineBay,
    isCreateMode
  )
  if (!engineValidation.isValid) {
    errors.push(engineValidation.message)
  }

  return {
    errors,
    isValid: errors.length === 0,
  }
}

export default validateVesselData
